import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useRoles } from "hooks/useRoles";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DataTable from "cxfcomponents/Tables/DataTable";
import RoleForm from "../RoleForm";
import { RoleActions } from "../RoleActions";
import TileView from "../TileView";
import TextWithEllipsis from "layouts/pages/logs/components/TextWithEllipsis";
import UsersCell from "layouts/Flows/components/UsersCell";
import DefaultAdminIcon from "assets/images/DefaultAdminIcon.svg";
import DefaultUserIcon from "assets/images/DefaultUserIcon.svg";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import { Link } from "react-router-dom";

const RolesTable = ({ splitted = true }) => {
	const { roles, handleFetchRoles, loading } = useRoles(false);
	const { users: allUsers = [] } = useSelector(
		(store) => store?.portal?.users
	);
	const [openAddRoleModal, setOpenAddRoleModal] = useState(false);
	const [tableView, setTableView] = useState("list");

	const handleTileView = (view) => {
		setTableView(view);
		localStorage.setItem("flowView", view);
	};

	const getRoleIcon = (roleName) => {
		const nameLower = roleName?.toLowerCase();
		if (nameLower === "admin") return DefaultAdminIcon;
		return DefaultUserIcon;
	};

	const dataTableData = useMemo(() => {
		const displayRoles = roles.filter(
			(role) => role.name?.toLowerCase() !== "owner"
		);

		return {
			columns: [
				{
					Header: "icon",
					accessor: "icon",
					width: 80,
					disableSortBy: true,
					Cell: ({ row }) => (
						<SoftBox
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								width: "60px",
								height: "60px"
							}}
						>
							<SoftBox
								component="img"
								src={row.original.image}
								alt={`${row.original.name} icon`}
								sx={{
									width: "60px",
									height: "60px",
									objectFit: "contain",
									borderRadius: "12px",
									border: "none"
								}}
							/>
						</SoftBox>
					)
				},
				{
					Header: "title",
					accessor: "name",
					width: 250,
					disableSortBy: false,
					defaultSortOrder: "asc",
					Cell: ({ row, value }) => (
						<SoftTypography
							variant="caption"
							fontWeight="medium"
							color="text"
							sx={{ fontSize: "0.875rem" }}
						>
							<Link
								style={{ color: "#2D3748" }}
								className="white-to-dark"
								to={`/administration/roles-and-permissions/${row.original.id}`}
							>
								{value}
							</Link>
						</SoftTypography>
					)
				},
				{
					Header: "description",
					accessor: "description",
					Cell: ({ value }) => (
						<TextWithEllipsis
							value={{
								name: value || "",
								maxWidth: "clamp(100px, 20vw, 300px)"
							}}
						/>
					)
				},
				{
					Header: "users",
					accessor: "users",
					Cell: ({ value }) => {
						return <UsersCell users={value} />;
					},
					disableSortBy: true
				}
			],
			rows: [...displayRoles]
				.sort((firstRole, secondRole) => (firstRole.name || "").localeCompare(secondRole.name || "", undefined, { sensitivity: "base" }))
				.map((role) => {
				const assignedUsers = allUsers.filter(
					(user) => user.role_id === role.id
				);
				const lastEditedFormatted = role.updated_date_time
					? formatDateAndTime(
							getLocalDateTimeString(role.updated_date_time)
						)
					: "";

				const originalData = {
					id: role.id,
					name: role.name,
					description: role.description,
					image: getRoleIcon(role.name),
					members: assignedUsers,
					users: assignedUsers,
					action: (
						<RoleActions
							selectedRole={role}
							tableView={tableView}
						/>
					),
					dateTime: lastEditedFormatted,
					lastEdited: lastEditedFormatted,
					status: null,
					badgeContent: "Role",
					color: "light",
					dropdown: false,
					flowId: role.id,
					schedule_id: null,
					publishStatus: null,
					sharedStatus: null,
					icon: getRoleIcon(role.name)
				};
				return {
					...originalData,
					action: originalData.action,
					users: originalData.users,
					icon: originalData.image,
					original: originalData
				};
			})
		};
	}, [roles, allUsers]);

	useEffect(() => {
		setTableView(localStorage.getItem("rolesView") ?? "list");
	}, []);

	return (
		<>
			<DataTable
				table={dataTableData}
				entriesPerPage={{ defaultValue: 25 }}
				canSort={true}
				showTotalEntries={true}
				hiddenTopButtons={false}
				loading={loading}
				splitted={splitted}
				withSearchbar={true}
				withTileview={true}
				reloadRoleList={handleFetchRoles}
				tableView={tableView}
				tileViewComponent={(rows) => (
					<TileView
						data={rows}
						groupBy={[]}
					/>
				)}
				setTableView={handleTileView}
				setOpenAddRoleModal={setOpenAddRoleModal}
				tableHeight="clamp(400px, calc(100vh - 84px), calc(100vh - 84px))"
			/>
			<RoleForm
				open={openAddRoleModal}
				onClose={() => setOpenAddRoleModal(false)}
				onSubmit={() => setOpenAddRoleModal(false)}
				mode="add"
			/>
		</>
	);
};

export default RolesTable;
